import axios from 'axios';

import { LocalStorage } from '@data/enums';

// Self-contained, authenticated calls to the inactive client alert endpoints.
//
// We intentionally do NOT add these to the shared dy-frontend-http-repository
// package: that would require pushing a third repo and bumping the lockfile.
// Instead we reuse the exact same configuration the app already uses — the
// API base URL from the build-time env and the bearer access token from
// localStorage (the same key the shared repository instance is seeded with).
//
// The backend is authoritative for every value here. Nothing on this path writes
// settings or notices to localStorage: what the server returns is what is shown.

export type ClientInactivityAlertConditionType = 'not_logged_in' | 'no_new_projects_submitted' | 'no_communications';

export interface ClientInactivityAlertSettingsResource {
    days_not_logged_in: number;
    days_no_new_projects_submitted: number;
    weeks_no_communications: number;
    is_enabled: boolean;
    is_email_enabled: boolean;
    is_slack_enabled: boolean;
    is_dashboard_enabled: boolean;
}

export interface ClientInactivityAlertNoticeConditionResource {
    type: ClientInactivityAlertConditionType;
    label: string;
    unit: 'day' | 'week';
    threshold: number;
    last_at: string | null;
    last_date: string | null;
}

export interface ClientInactivityAlertNoticeResource {
    id: string;
    client_user_id: string;
    generated_at: string;
    client_name: string;
    business_name: string;
    email: string;
    conditions: ClientInactivityAlertNoticeConditionResource[];
}

export interface ClientInactivityAlertActiveNoticeListResource {
    is_dashboard_enabled: boolean;
    items: ClientInactivityAlertNoticeResource[];
}

export type ClientInactivityAlertRunFindingOutcome = 'created' | 'superseded' | 'unchanged' | 'suppressed' | 'skipped';

// One client the run matched. Snowflakes are strings; notice_id is null when nothing stands
export interface ClientInactivityAlertRunFindingResource {
    client_user_id: string;
    notice_id: string | null;
    client_name: string;
    business_name: string;
    email: string;
    outcome: ClientInactivityAlertRunFindingOutcome;
    inactive_days: number;
    conditions: ClientInactivityAlertNoticeConditionResource[];
}

export interface ClientInactivityAlertRunResultResource {
    is_disabled: boolean;
    report_id: string | null;
    generated_at: string | null;
    evaluated_client_count: number;
    created_notice_count: number;
    superseded_notice_count: number;
    suppressed_notice_count: number;
    unchanged_notice_count: number;
    email_recipient_count: number;
    slack_message_ids: string[];
    // Most inactive first, as the server orders them
    findings: ClientInactivityAlertRunFindingResource[];
}

export interface ClientInactivityAlertClearResultResource {
    is_cleared: boolean;
    client_inactivity_alert_notice_id: string | null;
}

// The endpoint refuses more than this many IDs in one lookup, so the caller chunks
export const MAX_ACTIVE_NOTICE_LOOKUP_IDS = 200;

const getBaseUrl = () => (process.env.API_BASE_URL ?? '').replace(/\/+$/, '');

const getAuthHeaders = () => {
    const accessToken = localStorage.getItem(LocalStorage.ACCESS_TOKEN);
    return accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined;
};

export const getClientInactivityAlertSettings = async (): Promise<ClientInactivityAlertSettingsResource> => {
    const { data } = await axios.get(`${getBaseUrl()}/api/client-inactivity-alert/settings`, {
        headers: getAuthHeaders(),
    });

    return data;
};

// A full replacement over a closed key set: the endpoint rejects anything else with a 422,
// so this must send exactly these seven keys and no more
export const updateClientInactivityAlertSettings = async (
    settings: ClientInactivityAlertSettingsResource
): Promise<ClientInactivityAlertSettingsResource> => {
    const { data } = await axios.put(
        `${getBaseUrl()}/api/client-inactivity-alert/settings`,
        {
            days_not_logged_in: settings.days_not_logged_in,
            days_no_new_projects_submitted: settings.days_no_new_projects_submitted,
            weeks_no_communications: settings.weeks_no_communications,
            is_enabled: settings.is_enabled,
            is_email_enabled: settings.is_email_enabled,
            is_slack_enabled: settings.is_slack_enabled,
            is_dashboard_enabled: settings.is_dashboard_enabled,
        },
        { headers: { ...(getAuthHeaders() ?? {}), 'Content-Type': 'application/json' } }
    );

    return data;
};

// Synchronous, and the same application service the daily job runs
export const runClientInactivityAlertCheck = async (): Promise<ClientInactivityAlertRunResultResource> => {
    const { data } = await axios.post(
        `${getBaseUrl()}/api/client-inactivity-alert/run`,
        {},
        { headers: { ...(getAuthHeaders() ?? {}), 'Content-Type': 'application/json' } }
    );

    return data;
};

export const getClientInactivityAlertActiveNotices = async (
    clientUserIds: string[]
): Promise<ClientInactivityAlertActiveNoticeListResource> => {
    const ids = Array.from(new Set(clientUserIds)).slice(0, MAX_ACTIVE_NOTICE_LOOKUP_IDS);

    if (ids.length === 0) {
        return { is_dashboard_enabled: false, items: [] };
    }

    const { data } = await axios.get(`${getBaseUrl()}/api/client-inactivity-alert/notice/active`, {
        params: { client_user_id: ids },
        headers: getAuthHeaders(),
    });

    return { is_dashboard_enabled: data?.is_dashboard_enabled === true, items: data?.items ?? [] };
};

export const clearClientInactivityAlertNotice = async (
    clientUserId: string
): Promise<ClientInactivityAlertClearResultResource> => {
    const { data } = await axios.post(
        `${getBaseUrl()}/api/client-inactivity-alert/notice/client/${clientUserId}/clear`,
        {},
        { headers: { ...(getAuthHeaders() ?? {}), 'Content-Type': 'application/json' } }
    );

    return data;
};
